/**
 * 🔔 NOTIFICATION PREFERENCES DIALOG
 * Allows admins to manage user notification preferences for support purposes
 * Tracks changes for TCPA, GDPR, and CAN-SPAM compliance
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  Notifications,
  Email,
  Sms,
  PhoneIphone,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import userService from '@/services/userService';

interface NotificationPreferencesDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName?: string;
}

export const NotificationPreferencesDialog: React.FC<NotificationPreferencesDialogProps> = ({
  open,
  onClose,
  userId,
  userName,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState<{
    userName: string;
    email: string;
    phone: string;
    hasPushToken: boolean;
    pushPlatform?: string;
  } | null>(null);
  const [preferences, setPreferences] = useState({
    push: false,
    email: false,
    sms: false,
  });
  const [initialPreferences, setInitialPreferences] = useState({
    push: false,
    email: false,
    sms: false,
  });
  const [reason, setReason] = useState('');

  // Load user preferences when dialog opens
  useEffect(() => {
    if (open && userId) {
      loadPreferences();
    }
  }, [open, userId]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const result = await userService.getNotificationPreferences(userId);

      if (result.success && result.data) {
        setUserData({
          userName: result.data.userName,
          email: result.data.email,
          phone: result.data.phone,
          hasPushToken: result.data.hasPushToken,
          pushPlatform: result.data.pushPlatform,
        });
        setPreferences(result.data.preferences);
        setInitialPreferences(result.data.preferences);
      } else {
        toast.error(result.error || 'Failed to load notification preferences');
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Check if there are any changes
    const hasChanges =
      preferences.push !== initialPreferences.push ||
      preferences.email !== initialPreferences.email ||
      preferences.sms !== initialPreferences.sms;

    if (!hasChanges) {
      toast.error('No changes to save');
      return;
    }

    // Require reason for changes
    if (!reason.trim()) {
      toast.error('Please provide a reason for changing notification preferences');
      return;
    }

    setSaving(true);
    try {
      const result = await userService.updateNotificationPreferences(
        userId,
        preferences,
        reason
      );

      if (result.success) {
        toast.success(
          `Notification preferences updated successfully!\nChanges: ${result.data?.changes.join(', ') || 'Updated'}`
        );
        setInitialPreferences(preferences);
        setReason('');
        onClose();
      } else {
        toast.error(result.error || 'Failed to update notification preferences');
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Reset to initial values on close
    setPreferences(initialPreferences);
    setReason('');
    onClose();
  };

  const hasChanges =
    preferences.push !== initialPreferences.push ||
    preferences.email !== initialPreferences.email ||
    preferences.sms !== initialPreferences.sms;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Notifications color="primary" />
          <Typography variant="h6">Notification Preferences</Typography>
        </Box>
        {userData && (
          <Typography variant="caption" color="text.secondary">
            {userData.userName} ({userData.email})
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {/* User Info */}
            {userData && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="body2">{userData.email}</Typography>
                  </Box>
                  {userData.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Sms fontSize="small" color="action" />
                      <Typography variant="body2">{userData.phone}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIphone fontSize="small" color="action" />
                    <Typography variant="body2">
                      Push Token: {userData.hasPushToken ? (
                        <Chip
                          label={`✓ ${userData.pushPlatform || 'Registered'}`}
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip label="Not registered" size="small" color="default" />
                      )}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            {/* Compliance Notice */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Compliance Notice:</strong> Changes are logged for TCPA, GDPR, and CAN-SPAM compliance.
                Always provide a reason when updating preferences.
              </Typography>
            </Alert>

            {/* Notification Channels */}
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Notification Channels
            </Typography>

            <Stack spacing={2}>
              {/* Push Notifications */}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.push}
                      onChange={(e) => setPreferences({ ...preferences, push: e.target.checked })}
                      disabled={!userData?.hasPushToken}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        📱 Push Notifications
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userData?.hasPushToken
                          ? 'In-app notifications on mobile device'
                          : 'User has not registered a push token'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Divider />

              {/* Email Notifications */}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email}
                      onChange={(e) => setPreferences({ ...preferences, email: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        📧 Email Notifications
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Notifications sent to {userData?.email || 'user email'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Divider />

              {/* SMS Notifications */}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.sms}
                      onChange={(e) => setPreferences({ ...preferences, sms: e.target.checked })}
                      disabled={!userData?.phone}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        💬 SMS Notifications
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userData?.phone
                          ? `Text messages to ${userData.phone}`
                          : 'User has not provided a phone number'}
                      </Typography>
                    </Box>
                  }
                />
                {preferences.sms && (
                  <Alert severity="warning" sx={{ mt: 1, ml: 4 }}>
                    <Typography variant="caption">
                      ⚠️ SMS costs money. Only enable if user explicitly opted in via phone support.
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Stack>

            {/* Reason Field */}
            {hasChanges && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Reason for Change *"
                  placeholder="e.g., Customer requested SMS opt-in via phone support on 2025-01-10"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  helperText="Required for compliance audit trail. Include customer request details."
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || saving || !reason.trim()}
          startIcon={saving ? <CircularProgress size={20} /> : <CheckCircle />}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
